import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  FileText,
  Download,
  Calendar,
  Users,
  DollarSign,
  Calculator,
  Search,
  Package,
  Save,
  Loader2,
  Plus,
  Trash,
  Eye
} from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useAuth } from '../utils/AuthContext';
import { listCustomers } from '../utils/db/customers';
import CustomerAutocomplete from '../components/CustomerAutocomplete';
import { listBookingsForRange } from '../utils/db/bookings';
import { getNextInvoiceNo, createInvoice, getInvoice, getInvoiceItems, updateInvoice } from '../utils/db/invoices';
import InvoiceLayout from '../components/InvoiceLayout';
import { useReactToPrint } from 'react-to-print';
import { getProfile } from '../utils/db/profiles';
import { computeInvoiceCharges, sanitizePercentInput } from '../utils/money';

const InvoiceGenerator = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editInvoiceId = searchParams.get('edit');
  const [customers, setCustomers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // Use 3-digit invoice numbers by default
  const [nextInvoiceNo, setNextInvoiceNo] = useState('001');
  const [isEditMode, setIsEditMode] = useState(false);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [franchiseProfile, setFranchiseProfile] = useState(null);

  const [showPreview, setShowPreview] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [lastPreviewPayload, setLastPreviewPayload] = useState(null); // { invoiceData, selectedBookingsData }
  const printComponentRef = useRef();

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm();

  useEffect(() => {
    if (user) {
      loadData();
      // Load franchise profile from database
      loadFranchiseProfile();
    }

    // Cleanup function
    return () => {
      // Cleanup when component unmounts
    };
  }, [user, editInvoiceId]);

  const toThreeDigit = (val) => {
    const n = parseInt(String(val), 10);
    return (Number.isFinite(n) ? n : 1).toString().padStart(3, '0');
  };

  const loadData = async () => {
    try {
      const [custs, nextNo] = await Promise.all([
        listCustomers(user.id),
        getNextInvoiceNo(user.id)
      ]);
      setCustomers(custs);

      if (editInvoiceId) {
        setIsEditMode(true);
        const invoice = await getInvoice(user.id, editInvoiceId);
        const items = await getInvoiceItems(user.id, editInvoiceId);

        if (invoice) {
          // Debug: Log the invoice data to see what we're working with
          console.log('Loading invoice for edit:', invoice);

          // Set form values from invoice data
          const formData = {
            invoiceNo: invoice.invoice_no ? (invoice.invoice_no.match(/(\d+)$/)?.[0] || '') : '',
            customerId: invoice.customer_id,
            invoiceDate: invoice.invoice_date ? new Date(invoice.invoice_date).toISOString().split('T')[0] : '',
            fromDate: invoice.from_date ? new Date(invoice.from_date).toISOString().split('T')[0] : '',
            toDate: invoice.to_date ? new Date(invoice.to_date).toISOString().split('T')[0] : '',
            fuelCharge: invoice.fuel_percent || '',
            royaltyCharge: invoice.royalty_percent || '',
            docketCharge: invoice.docket_charge || '',
            odaCharge: invoice.oda_charge || '',
            otherCharge: invoice.other_charge || '',
            gstPercent: invoice.gst_percent || '0'
          };

          console.log('Setting form data:', formData);
          reset(formData);

          // Debug: Check if form values are set after reset
          setTimeout(() => {
            console.log('Form values after reset:', {
              invoiceNo: watch('invoiceNo'),
              customerId: watch('customerId'),
              invoiceDate: watch('invoiceDate'),
              fromDate: watch('fromDate'),
              toDate: watch('toDate'),
              fuelCharge: watch('fuelCharge'),
              royaltyCharge: watch('royaltyCharge'),
              docketCharge: watch('docketCharge'),
              odaCharge: watch('odaCharge'),
              otherCharge: watch('otherCharge'),
              gstPercent: watch('gstPercent')
            });
          }, 100);

          setNextInvoiceNo(invoice.invoice_no);
          setInvoiceItems(items);

          // Load bookings for this customer and date range
          const bookingsData = await listBookingsForRange(
            user.id,
            invoice.customer_id,
            invoice.from_date,
            invoice.to_date
          );

          setBookings(bookingsData);
          setFilteredBookings(bookingsData);

          // Select bookings that are in the invoice
          const bookingIds = items
            .filter(item => item.booking_id)
            .map(item => item.booking_id);

          console.log('Setting selected bookings:', bookingIds);
          setSelectedBookings(bookingIds);
        }
      } else {
        const formatted = toThreeDigit(nextNo);
        setNextInvoiceNo(formatted);
        // Ensure the form field is populated (react-hook-form defaultValue won't update after first render)
        try {
          setValue('invoiceNo', formatted, { shouldValidate: true });
        } catch (e) {
          // ignore if setValue not available yet
        }
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load initial data');
    }
  };

  const loadFranchiseProfile = async () => {
    try {
      const profile = await getProfile(user.id);
      if (profile) {
        // Transform database fields to match the expected format
        const transformedProfile = {
          franchiseName: profile.franchise_name || 'TATYS',
          address: profile.address || 'NO 354, T H ROAD THIRUMAZHISAI - 600124',
          mobileNo: profile.mobile_no || '8015695652',
          email: profile.email || 'thirumazhisai.dist@gmail.com',
          gstNo: profile.gst_no || '27CIOPP1874N1Z',
          panNo: profile.pan_no || 'CIOPP1874N',
          bankName: profile.bank_name || 'Canara Bank',
          accountNo: profile.account_no || '102000820462',
          ifsc: profile.ifsc || 'CNRB0003072',
          branchName: profile.branch_name || 'THIRUMAZHISAI',
          terms: profile.terms ? profile.terms.split('\n') : [
            'Payment should be made by cheque in favour of TATYS',
            'Any queries must be forwarded within 15 days of receipt',
            '18% interest will be charged on overdue bills'
          ],
          invoicePrefix: profile.invoice_prefix || 'INV',
          logoUrl: profile.logo_url || null
        };
        setFranchiseProfile(transformedProfile);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      // Fallback to default profile
      setFranchiseProfile({
        franchiseName: 'TATYS',
        address: 'NO 354, T H ROAD THIRUMAZHISAI - 600124',
        mobileNo: '8015695652',
        email: 'thirumazhisai.dist@gmail.com',
        gstNo: '27CIOPP1874N1Z',
        panNo: 'CIOPP1874N',
        bankName: 'Canara Bank',
        accountNo: '102000820462',
        ifsc: 'CNRB0003072',
        branchName: 'THIRUMAZHISAI',
        terms: [
          'Payment should be made by cheque in favour of TATYS',
          'Any queries must be forwarded within 15 days of receipt',
          '18% interest will be charged on overdue bills'
        ]
      });
    }
  };

  const selectedCustomerId = watch('customerId');
  const fromDate = watch('fromDate');
  const toDate = watch('toDate');

  useEffect(() => {
    const run = async () => {
      if (user && selectedCustomerId && fromDate && toDate) {
        try {
          const rows = await listBookingsForRange(user.id, selectedCustomerId, fromDate, toDate);
          setBookings(rows);
          setFilteredBookings(rows);
          // Preselect all available bookings for new invoices; keep existing selection for edit mode
          if (!isEditMode) {
            setSelectedBookings(rows.map(b => b.id));
          }
          // Don't clear selected bookings in edit mode - they should be set by the first useEffect
        } catch (e) {
          toast.error('Failed to load bookings');
        }
      } else {
        setFilteredBookings([]);
        setSelectedBookings([]);
      }
    };
    run();
  }, [user, selectedCustomerId, fromDate, toDate]);

  const toggleBookingSelection = (bookingId) => {
    setSelectedBookings(prev => {
      if (prev.includes(bookingId)) {
        return prev.filter(id => id !== bookingId);
      } else {
        return [...prev, bookingId];
      }
    });
  };

  const selectAllBookings = () => {
    setSelectedBookings(filteredBookings.map(booking => booking.id));
  };

  const clearSelection = () => {
    setSelectedBookings([]);
  };

  const calculateConsignmentAmount = () => {
    return selectedBookings.reduce((sum, bookingId) => {
      const booking = filteredBookings.find(b => b.id === bookingId);
      const amount = parseFloat(booking?.amount) || 0;
      const otherCharge = parseFloat(booking?.other_charge) || 0;
      return sum + amount + otherCharge;
    }, 0);
  };

  const calculateTaxableBase = () => {
    const consignmentAmount = calculateConsignmentAmount();
    const docketCharge = parseFloat(watch('docketCharge')) || 0;
    const odaCharge = parseFloat(watch('odaCharge')) || 0;
    const otherCharge = parseFloat(watch('otherCharge')) || 0;
    return consignmentAmount + docketCharge + odaCharge + otherCharge;
  };

  // Completely recreate the PDF generation function to ensure it uses the latest layout
  const generatePDF = (invoiceData) => {
    console.log('Generating PDF with updated layout');
    // Create a new jsPDF instance with cache-busting timestamp
    const doc = new jsPDF('p', 'mm', 'a4');
    // Add a timestamp to force refresh
    const timestamp = new Date().getTime();
    const franchiseProfile = JSON.parse(localStorage.getItem('franchiseProfile') || '{}');
    const selectedCustomer = customers.find(c => c.id === invoiceData.customerId);

    const getDaysInclusive = (from, to) => {
      try {
        const d1 = new Date(from);
        const d2 = new Date(to);
        const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
        return (isNaN(diff) ? 0 : diff) + 1;
      } catch {
        return 0;
      }
    };

    // Clear any previous document cache
    if (window._previousPdfInstance) {
      delete window._previousPdfInstance;
    }

    // Store this instance for debugging
    window._previousPdfInstance = doc;

    // Force jsPDF to use the latest version
    doc._version = timestamp;

    // Set document properties
    doc.setProperties({
      title: `Invoice ${invoiceData.invoiceNo}`,
      subject: 'Invoice',
      author: franchiseProfile.franchiseName || 'ShipNet Express',
      keywords: 'invoice, shipping, logistics',
      creator: 'ShipNet Express Billing System'
    });

    // Normalize bookings to snake_case fields
    const selectedBookingsData = filteredBookings
      .filter(b => selectedBookings.includes(b.id))
      .map(b => ({
        date: b.date,
        awb_no: b.awb_no || b.awbNo,
        receiver_name: b.receiver_name || b.receiverName,
        receiver_address: b.receiver_address || b.receiverAddress,
        mode: b.mode,
        weight: parseFloat(b.weight || 0),
        quantity: parseInt(b.quantity || 0, 10),
        type: b.type || '',
        amount: parseFloat(b.amount || 0),
      }));

    // Use the already calculated values from invoiceData to ensure consistency
    const bookingSubtotal = selectedBookingsData.reduce((s, b) => s + (b.amount || 0), 0);
    const fuelCharge = parseFloat(invoiceData.fuelCharge) || 0;
    const royaltyCharge = parseFloat(invoiceData.royaltyCharge) || 0;
    const docketCharge = parseFloat(invoiceData.docketCharge) || 0;
    const odaCharge = parseFloat(invoiceData.odaCharge) || 0;
    const otherCharge = parseFloat(invoiceData.otherCharge) || 0;
    const userGstPercent = parseFloat(invoiceData.gstPercent) || 0;
    const totalCharges = fuelCharge + royaltyCharge + docketCharge + odaCharge + otherCharge;
    const totalBeforeGST = parseFloat(invoiceData.totalBeforeGST) || (bookingSubtotal + totalCharges);

    // Use the already calculated GST values from invoiceData to ensure consistency
    const gstAmount = parseFloat(invoiceData.gstCalculation?.totalGST) || 0;
    const grandTotal = parseFloat(invoiceData.grandTotal) || (totalBeforeGST + gstAmount);

    // HEADER - Company Logo and Details with modern blue design
    let y = 10;
    // Draw filled blue header background
    doc.setFillColor(79, 70, 229); // Modern indigo color
    doc.rect(10, y, 190, 30, 'F');

    // Add logo
    if (franchiseProfile.logo) {
      doc.addImage(franchiseProfile.logo, 'PNG', 15, y + 5, 35, 18);
    } else {
      // If no logo, add text placeholder with white text
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 255, 255); // White text
      doc.text('ShipNet', 30, y + 15);
      doc.text('EXPRESS', 30, y + 22);
    }

    // Add company details with white text
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(255, 255, 255); // White text
    doc.text(`NO 354, THIRUVANMIYUR - 600124`, 110, y + 5);

    doc.text(`Mobile No: ${franchiseProfile.mobileNo || '9876543210'}`, 110, y + 10);
    doc.text(`Email: ${franchiseProfile.email || 'thiruvanmiyur.ship@gmail.com'}`, 110, y + 15);
    doc.text(`GST No: ${franchiseProfile.gstNo || '27CQGPP1847N1ZC'}`, 110, y + 20);

    // Reset text color to black for the rest of the document
    doc.setTextColor(0, 0, 0);

    // CUSTOMER SECTION with modern blue design
    y = 42;
    // Left section - Customer details with blue header
    doc.setFillColor(79, 70, 229); // Modern indigo color
    doc.rect(10, y, 130, 8, 'F');
    doc.setTextColor(255, 255, 255); // White text
    doc.setFont('helvetica', 'bold');
    doc.text('CUSTOMER DETAILS', 75, y + 5, { align: 'center' });

    // Customer details box with light blue background
    doc.setFillColor(238, 242, 255); // Light indigo background
    doc.rect(10, y + 8, 130, 22, 'F');
    doc.rect(10, y, 130, 30, 'S'); // Border around entire section

    doc.setTextColor(0, 0, 0); // Reset to black text
    doc.text('M/s', 15, y + 15);
    doc.setFont('helvetica', 'normal');
    doc.text(`${selectedCustomer?.customer_name || 'Ramesh Collection'}`, 30, y + 15);
    doc.text(`${selectedCustomer?.address || 'Thiruvanmiyur Sagar Chittinaidu puram'}`, 30, y + 20);
    doc.text(`GST No: ${selectedCustomer?.gst_no || '33A5523425'}`, 15, y + 25);

    // Right section - Invoice details with blue header
    doc.setFillColor(79, 70, 229); // Modern indigo color
    doc.rect(140, y, 60, 8, 'F');
    doc.setTextColor(255, 255, 255); // White text
    doc.setFont('helvetica', 'bold');
    doc.text('INVOICE DETAILS', 170, y + 5, { align: 'center' });

    // Invoice details box with light blue background
    doc.setFillColor(238, 242, 255); // Light indigo background
    doc.rect(140, y + 8, 60, 22, 'F');
    doc.rect(140, y, 60, 30, 'S'); // Border around entire section

    doc.setTextColor(0, 0, 0); // Reset to black text
    doc.text('Invoice No:', 145, y + 15);
    doc.text('Invoice Date:', 145, y + 19);
    doc.text('From Date:', 145, y + 23);
    doc.text('To Date:', 145, y + 27);

    doc.setFont('helvetica', 'normal');
    doc.text(`${invoiceData.invoiceNo}`, 195, y + 15, { align: 'right' });
    doc.text(`${invoiceData.invoiceDate}`, 195, y + 19, { align: 'right' });
    doc.text(`${invoiceData.fromDate}`, 195, y + 23, { align: 'right' });
    doc.text(`${invoiceData.toDate}`, 195, y + 27, { align: 'right' });

    // SUMMARY ROW with modern blue design
    y = 74;
    // Summary row headers with blue background
    doc.setFillColor(79, 70, 229); // Modern indigo color

    // Invoice Days header
    doc.rect(10, y, 45, 10, 'F');
    doc.rect(10, y, 45, 10, 'S');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255); // White text
    doc.text('INVOICE DAYS', 32.5, y + 6, { align: 'center' });

    // Total Consignment header
    doc.rect(55, y, 45, 10, 'F');
    doc.rect(55, y, 45, 10, 'S');
    doc.text('TOTAL CONSIGNMENT', 77.5, y + 6, { align: 'center' });

    // Total Weight header
    doc.rect(100, y, 45, 10, 'F');
    doc.rect(100, y, 45, 10, 'S');
    doc.text('TOTAL WEIGHT', 122.5, y + 6, { align: 'center' });

    // Consignment Amount header
    doc.rect(145, y, 55, 10, 'F');
    doc.rect(145, y, 55, 10, 'S');
    doc.text('CONSIGNMENT AMOUNT', 172.5, y + 6, { align: 'center' });

    // SUMMARY VALUES with light blue background
    y = 84;
    doc.setFillColor(238, 242, 255); // Light indigo background
    doc.setTextColor(0, 0, 0); // Reset to black text

    // Invoice Days value
    doc.rect(10, y, 45, 10, 'F');
    doc.rect(10, y, 45, 10, 'S');
    doc.setFont('helvetica', 'normal');
    const invoiceDays = getDaysInclusive(invoiceData.fromDate, invoiceData.toDate);
    doc.text(String(invoiceDays), 32.5, y + 6, { align: 'center' });

    // Total Consignment value
    doc.rect(55, y, 45, 10, 'F');
    doc.rect(55, y, 45, 10, 'S');
    const totalQty = selectedBookingsData.length;
    doc.text(String(totalQty), 77.5, y + 6, { align: 'center' });

    // Total Weight value
    doc.rect(100, y, 45, 10, 'F');
    doc.rect(100, y, 45, 10, 'S');
    const totalWeight = selectedBookingsData.reduce((s, b) => s + (parseFloat(b.weight) || 0), 0);
    doc.text(`${totalWeight.toFixed(2)} kg`, 122.5, y + 6, { align: 'center' });

    // Consignment Amount value
    doc.rect(145, y, 55, 10, 'F');
    doc.rect(145, y, 55, 10, 'S');
    doc.text(`₹${bookingSubtotal.toFixed(2)}`, 172.5, y + 6, { align: 'center' });

    // CHARGES GRID with modern blue design
    y = 96;
    // Right side charges grid with blue header
    doc.setFillColor(79, 70, 229); // Modern indigo color
    doc.rect(145, y - 8, 55, 8, 'F');
    doc.rect(145, y - 8, 55, 8, 'S');
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255); // White text
    doc.text('CHARGES SUMMARY', 172.5, y - 3, { align: 'center' });
    doc.setTextColor(0, 0, 0); // Reset to black text

    // Charges grid with alternating row colors
    // Consignment Amount
    doc.setFillColor(248, 250, 255); // Very light indigo
    doc.rect(145, y, 35, 10, 'F');
    doc.rect(145, y, 35, 10, 'S');
    doc.setFont('helvetica', 'bold');
    doc.text('Consignment Amount', 147, y + 6);
    doc.rect(180, y, 20, 10, 'F');
    doc.rect(180, y, 20, 10, 'S');
    doc.setFont('helvetica', 'normal');
    doc.text(`${bookingSubtotal.toFixed(2)}`, 195, y + 6, { align: 'right' });

    // FOV Charges
    y += 10;
    doc.setFillColor(235, 245, 255); // Light blue
    doc.rect(145, y, 35, 10, 'F');
    doc.rect(145, y, 35, 10, 'S');
    doc.setFont('helvetica', 'bold');
    doc.text('FOV Charges', 147, y + 6);
    doc.rect(180, y, 20, 10, 'F');
    doc.rect(180, y, 20, 10, 'S');
    doc.setFont('helvetica', 'normal');
    doc.text(`0.00`, 195, y + 6, { align: 'right' });

    // Royalty Charges
    y += 10;
    doc.setFillColor(248, 250, 255); // Very light indigo
    doc.rect(145, y, 35, 10, 'F');
    doc.rect(145, y, 35, 10, 'S');
    doc.setFont('helvetica', 'bold');
    doc.text('Royalty Charges', 147, y + 6);
    doc.rect(180, y, 20, 10, 'F');
    doc.rect(180, y, 20, 10, 'S');
    doc.setFont('helvetica', 'normal');
    doc.text(`${royaltyCharge.toFixed(2)}`, 195, y + 6, { align: 'right' });

    // Special Charge (ODA)
    y += 10;
    doc.setFillColor(235, 245, 255); // Light blue
    doc.rect(145, y, 35, 10, 'F');
    doc.rect(145, y, 35, 10, 'S');
    doc.setFont('helvetica', 'bold');
    doc.text('Special Charge(ODA)', 147, y + 6);
    doc.rect(180, y, 20, 10, 'F');
    doc.rect(180, y, 20, 10, 'S');
    doc.setFont('helvetica', 'normal');
    doc.text(`${odaCharge.toFixed(2)}`, 195, y + 6, { align: 'right' });

    // Docket Charges
    y += 10;
    doc.setFillColor(248, 250, 255); // Very light indigo
    doc.rect(145, y, 35, 10, 'F');
    doc.rect(145, y, 35, 10, 'S');
    doc.setFont('helvetica', 'bold');
    doc.text('Docket Charges', 147, y + 6);
    doc.rect(180, y, 20, 10, 'F');
    doc.rect(180, y, 20, 10, 'S');
    doc.setFont('helvetica', 'normal');
    doc.text(`${docketCharge.toFixed(2)}`, 195, y + 6, { align: 'right' });

    // Discount
    y += 10;
    doc.setFillColor(235, 245, 255); // Light blue
    doc.rect(145, y, 35, 10, 'F');
    doc.rect(145, y, 35, 10, 'S');
    doc.setFont('helvetica', 'bold');
    doc.text('Discount (0 %)', 147, y + 6);
    doc.rect(180, y, 20, 10, 'F');
    doc.rect(180, y, 20, 10, 'S');
    doc.setFont('helvetica', 'normal');
    doc.text(`0.00`, 195, y + 6, { align: 'right' });

    // Fuel Charges
    y += 10;
    doc.setFillColor(248, 250, 255); // Very light indigo
    doc.rect(145, y, 35, 10, 'F');
    doc.rect(145, y, 35, 10, 'S');
    doc.setFont('helvetica', 'bold');
    doc.text('Fuel Charges', 147, y + 6);
    doc.rect(180, y, 20, 10, 'F');
    doc.rect(180, y, 20, 10, 'S');
    doc.setFont('helvetica', 'normal');
    doc.text(`${fuelCharge.toFixed(2)}`, 195, y + 6, { align: 'right' });

    // Other Charges
    y += 10;
    doc.setFillColor(235, 245, 255); // Light blue
    doc.rect(145, y, 35, 10, 'F');
    doc.rect(145, y, 35, 10, 'S');
    doc.setFont('helvetica', 'bold');
    doc.text('Other Charges', 147, y + 6);
    doc.rect(180, y, 20, 10, 'F');
    doc.rect(180, y, 20, 10, 'S');
    doc.setFont('helvetica', 'normal');
    doc.text(`${otherCharge.toFixed(2)}`, 195, y + 6, { align: 'right' });

    // Sub Total with darker blue highlight
    y += 10;
    doc.setFillColor(224, 231, 255); // Medium light indigo
    doc.rect(145, y, 35, 10, 'F');
    doc.rect(145, y, 35, 10, 'S');
    doc.setFont('helvetica', 'bold');
    doc.text('Sub Total', 147, y + 6);
    doc.rect(180, y, 20, 10, 'F');
    doc.rect(180, y, 20, 10, 'S');
    doc.setFont('helvetica', 'normal');
    doc.text(`${totalBeforeGST.toFixed(2)}`, 195, y + 6, { align: 'right' });

    // Display GST based on customer state using values from invoiceData
    if (selectedCustomer?.customer_state === 'same') {
      // Same state: Show CGST and SGST using values from invoiceData
      const cgstPercent = parseFloat(invoiceData.gstCalculation?.cgstPercent) || (userGstPercent / 2);
      const sgstPercent = parseFloat(invoiceData.gstCalculation?.sgstPercent) || (userGstPercent / 2);
      const cgstAmount = parseFloat(invoiceData.gstCalculation?.cgstAmount) || 0;
      const sgstAmount = parseFloat(invoiceData.gstCalculation?.sgstAmount) || 0;

      // CGST
      y += 10;
      doc.setFillColor(248, 250, 255); // Very light indigo
      doc.rect(145, y, 35, 10, 'F');
      doc.rect(145, y, 35, 10, 'S');
      doc.setFont('helvetica', 'bold');
      doc.text(`CGST @ ${cgstPercent}%`, 147, y + 6);
      doc.rect(180, y, 20, 10, 'F');
      doc.rect(180, y, 20, 10, 'S');
      doc.setFont('helvetica', 'normal');
      doc.text(`${cgstAmount.toFixed(2)}`, 195, y + 6, { align: 'right' });

      // SGST
      y += 10;
      doc.setFillColor(235, 245, 255); // Light blue
      doc.rect(145, y, 35, 10, 'F');
      doc.rect(145, y, 35, 10, 'S');
      doc.setFont('helvetica', 'bold');
      doc.text(`SGST @ ${sgstPercent}%`, 147, y + 6);
      doc.rect(180, y, 20, 10, 'F');
      doc.rect(180, y, 20, 10, 'S');
      doc.setFont('helvetica', 'normal');
      doc.text(`${sgstAmount.toFixed(2)}`, 195, y + 6, { align: 'right' });
    } else {
      // Other state: Show IGST using values from invoiceData
      const igstPercent = parseFloat(invoiceData.gstCalculation?.igstPercent) || userGstPercent;
      const igstAmount = parseFloat(invoiceData.gstCalculation?.igstAmount) || gstAmount;
      y += 10;
      doc.setFillColor(248, 250, 255); // Very light indigo
      doc.rect(145, y, 35, 10, 'F');
      doc.rect(145, y, 35, 10, 'S');
      doc.setFont('helvetica', 'bold');
      doc.text(`IGST @ ${igstPercent}%`, 147, y + 6);
      doc.rect(180, y, 20, 10, 'F');
      doc.rect(180, y, 20, 10, 'S');
      doc.setFont('helvetica', 'normal');
      doc.text(`${igstAmount.toFixed(2)}`, 195, y + 6, { align: 'right' });
    }

    // Round Off
    y += 10;
    doc.setFillColor(235, 245, 255); // Light blue
    doc.rect(145, y, 35, 10, 'F');
    doc.rect(145, y, 35, 10, 'S');
    doc.setFont('helvetica', 'bold');
    doc.text('Round Off', 147, y + 6);
    doc.rect(180, y, 20, 10, 'F');
    doc.rect(180, y, 20, 10, 'S');
    doc.setFont('helvetica', 'normal');
    doc.text(`0.00`, 195, y + 6, { align: 'right' });

    // Total Amount with blue highlight
    y += 10;
    doc.setFillColor(79, 70, 229); // Modern indigo color
    doc.rect(145, y, 35, 10, 'F');
    doc.rect(145, y, 35, 10, 'S');
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255); // White text
    doc.text('Total Amount', 147, y + 6);
    doc.rect(180, y, 20, 10, 'F');
    doc.rect(180, y, 20, 10, 'S');
    doc.setTextColor(0, 0, 0); // Reset to black text
    doc.text(`${grandTotal.toFixed(2)}`, 195, y + 6, { align: 'right' });

    // TERMS & BANK DETAILS with modern blue design
    y = 124;
    // Terms section with blue header
    doc.setFillColor(79, 70, 229); // Modern indigo color
    doc.rect(8, y, 97, 8, 'F');
    doc.rect(8, y, 97, 8, 'S');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255); // White text
    doc.text('TERMS & CONDITIONS', 56.5, y + 5, { align: 'center' });

    // Terms content with light blue background
    doc.setFillColor(238, 242, 255); // Light indigo background
    doc.rect(8, y + 8, 97, 22, 'F');
    doc.rect(8, y + 8, 97, 22, 'S'); // Border around content
    doc.setTextColor(0, 0, 0); // Reset to black text
    doc.setFont('helvetica', 'normal');
    const termsText = franchiseProfile.terms || '1) Payment Should be made by cheque in favour of us\n2) All queries in respect of this bill must be forwarded within 15 days of receipt.\n3) TDS withheld will be charged on bills not paid within a month';
    const termsLines = doc.splitTextToSize(termsText, 90);
    doc.text(termsLines, 12, y + 15);

    // Bank details section with blue header
    y = 154;
    doc.setFillColor(79, 70, 229); // Modern indigo color
    doc.rect(8, y, 97, 8, 'F');
    doc.rect(8, y, 97, 8, 'S');
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255); // White text
    doc.text('OUR BANK DETAILS', 56.5, y + 5, { align: 'center' });

    // Bank details content with light blue background
    doc.setFillColor(238, 242, 255); // Light indigo background
    doc.rect(8, y + 8, 97, 12, 'F');
    doc.rect(8, y + 8, 97, 12, 'S'); // Border around content
    doc.setTextColor(0, 0, 0); // Reset to black text
    doc.setFont('helvetica', 'normal');
    doc.text(`NAME: ${franchiseProfile.bankName || 'Canara Bank'}`, 12, y + 13);
    doc.text(`A/C NO: ${franchiseProfile.accountNo || '0434101049319'}`, 12, y + 17);
    doc.text(`IFSC CODE: ${franchiseProfile.ifsc || 'CNRB0000375'}`, 56, y + 13);
    doc.text(`BRANCH: ${franchiseProfile.branchName || 'THIRUVANMIYUR'}`, 56, y + 17);

    // Signature/Stamp box with modern blue design
    y = 124;
    // Signature box with blue header
    doc.setFillColor(79, 70, 229); // Modern indigo color
    doc.rect(105, y, 97, 8, 'F');
    doc.rect(105, y, 97, 8, 'S');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255); // White text
    doc.text('AUTHORIZED SIGNATORY', 153.5, y + 5, { align: 'center' });

    // Signature content with light blue background
    doc.setFillColor(238, 242, 255); // Light indigo background
    doc.rect(105, y + 8, 97, 42, 'F');
    doc.rect(105, y + 8, 97, 42, 'S'); // Border around content
    doc.setTextColor(0, 0, 0); // Reset to black text
    doc.setFontSize(10);
    doc.text('For ShipNet Express', 153, y + 40, { align: 'center' });
    // Add a circular stamp image if available
    if (franchiseProfile.stampImage) {
      doc.addImage(franchiseProfile.stampImage, 'PNG', 138, y + 15, 30, 30);
    }

    // Footer: Total and page number (first page)
    doc.setFont('helvetica', 'bold');
    doc.text('Total', 12, 265);
    doc.setFont('helvetica', 'normal');
    doc.text(grandTotal.toFixed(2), 30, 265);

    // SECOND PAGE - BOOKINGS TABLE with modern blue design
    doc.addPage();
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');

    // Page title with blue background
    y = 10;
    doc.setFillColor(79, 70, 229); // Modern indigo color
    doc.rect(10, y, 190, 8, 'F');
    doc.rect(10, y, 190, 8, 'S');
    doc.setTextColor(255, 255, 255); // White text
    doc.setFontSize(12);
    doc.text('BOOKING DETAILS', 105, y + 5, { align: 'center' });
    doc.setFontSize(10);

    // Table header with blue background
    y = 20;
    // Header row
    doc.setFillColor(79, 70, 229); // Modern indigo color

    doc.rect(10, y, 15, 10, 'F');
    doc.rect(10, y, 15, 10, 'S');
    doc.text('Sr.', 17.5, y + 6, { align: 'center' });
    doc.text('No.', 17.5, y + 10, { align: 'center' });

    doc.rect(25, y, 25, 10, 'F');
    doc.rect(25, y, 25, 10, 'S');
    doc.text('Booking', 37.5, y + 6, { align: 'center' });
    doc.text('date', 37.5, y + 10, { align: 'center' });

    doc.rect(50, y, 25, 10, 'F');
    doc.rect(50, y, 25, 10, 'S');
    doc.text('Docket No.', 62.5, y + 8, { align: 'center' });

    doc.rect(75, y, 25, 10, 'F');
    doc.rect(75, y, 25, 10, 'S');
    doc.text('Receiver', 87.5, y + 8, { align: 'center' });

    doc.rect(100, y, 25, 10, 'F');
    doc.rect(100, y, 25, 10, 'S');
    doc.text('Destination', 112.5, y + 8, { align: 'center' });

    doc.rect(125, y, 15, 10, 'F');
    doc.rect(125, y, 15, 10, 'S');
    doc.text('Mode', 132.5, y + 8, { align: 'center' });

    doc.rect(140, y, 15, 10, 'F');
    doc.rect(140, y, 15, 10, 'S');
    doc.text('Wt.', 147.5, y + 8, { align: 'center' });

    doc.rect(155, y, 15, 10, 'F');
    doc.rect(155, y, 15, 10, 'S');
    doc.text('Qty', 162.5, y + 8, { align: 'center' });

    doc.rect(170, y, 15, 10, 'F');
    doc.rect(170, y, 15, 10, 'S');
    doc.text('Type', 177.5, y + 8, { align: 'center' });

    doc.rect(185, y, 15, 10, 'F');
    doc.rect(185, y, 15, 10, 'S');
    doc.text('Amount', 192.5, y + 8, { align: 'center' });

    // Reset text color to black for the table data
    doc.setTextColor(0, 0, 0);

    // Table rows with alternating colors
    y += 10;
    doc.setFont('helvetica', 'normal');
    let totalAmount = 0;
    selectedBookingsData.forEach((booking, i) => {
      const rowY = y + i * 8;

      // Set alternating row colors
      if (i % 2 === 0) {
        doc.setFillColor(245, 250, 255); // Very light blue
      } else {
        doc.setFillColor(235, 245, 255); // Light blue
      }

      // Sr. No.
      doc.rect(10, rowY, 15, 8, 'F');
      doc.rect(10, rowY, 15, 8, 'S');
      doc.text(String(i + 1), 17.5, rowY + 5, { align: 'center' });

      // Booking date
      doc.rect(25, rowY, 25, 8, 'F');
      doc.rect(25, rowY, 25, 8, 'S');
      doc.text(booking.date || '', 37.5, rowY + 5, { align: 'center' });

      // Docket No.
      doc.rect(50, rowY, 25, 8, 'F');
      doc.rect(50, rowY, 25, 8, 'S');
      doc.text(booking.awb_no || '', 62.5, rowY + 5, { align: 'center' });

      // Receiver
      doc.rect(75, rowY, 25, 8, 'F');
      doc.rect(75, rowY, 25, 8, 'S');
      doc.text(booking.receiver_name || '', 87.5, rowY + 5, { align: 'center' });

      // Destination
      doc.rect(100, rowY, 25, 8, 'F');
      doc.rect(100, rowY, 25, 8, 'S');
      doc.text(booking.receiver_address || '', 112.5, rowY + 5, { align: 'center' });

      // Mode
      doc.rect(125, rowY, 15, 8, 'F');
      doc.rect(125, rowY, 15, 8, 'S');
      doc.text(booking.mode || '', 132.5, rowY + 5, { align: 'center' });

      // Weight
      doc.rect(140, rowY, 15, 8, 'F');
      doc.rect(140, rowY, 15, 8, 'S');
      doc.text(String(booking.weight || ''), 147.5, rowY + 5, { align: 'center' });

      // Quantity
      doc.rect(155, rowY, 15, 8, 'F');
      doc.rect(155, rowY, 15, 8, 'S');
      doc.text(String(booking.quantity || ''), 162.5, rowY + 5, { align: 'center' });

      // Type
      doc.rect(170, rowY, 15, 8, 'F');
      doc.rect(170, rowY, 15, 8, 'S');
      doc.text(booking.type || '', 177.5, rowY + 5, { align: 'center' });

      // Amount
      doc.rect(185, rowY, 15, 8, 'F');
      doc.rect(185, rowY, 15, 8, 'S');
      const amount = booking.amount || 0;
      totalAmount += parseFloat(amount);
      doc.text(amount.toFixed(2), 192.5, rowY + 5, { align: 'center' });
    });

    // Total row with blue highlight
    const totalRowY = y + selectedBookingsData.length * 8;

    // Total label with blue background
    doc.setFillColor(59, 130, 246); // Modern blue color
    doc.rect(10, totalRowY, 175, 8, 'F');
    doc.rect(10, totalRowY, 175, 8, 'S');
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(255, 255, 255); // White text
    doc.text('TOTAL', 17.5, totalRowY + 5, { align: 'left' });

    // Total amount with blue background
    doc.rect(185, totalRowY, 15, 8, 'F');
    doc.rect(185, totalRowY, 15, 8, 'S');
    doc.text(totalAmount.toFixed(2), 192.5, totalRowY + 5, { align: 'center' });

    // Reset text color to black
    doc.setTextColor(0, 0, 0);

    // Page number with modern blue footer
    doc.setFillColor(59, 130, 246); // Modern blue color
    doc.rect(10, 275, 190, 10, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255); // White text
    doc.text(`Page 2 of ${doc.getNumberOfPages()}`, 105, 281, { align: 'center' });
    doc.setTextColor(0, 0, 0); // Reset to black text

    // Page numbers with modern blue footer on first page
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      if (i === 1) {
        // Add modern blue footer on first page
        doc.setFillColor(59, 130, 246); // Modern blue color
        doc.rect(10, 280, 190, 10, 'F');
        doc.setFontSize(8);
        doc.setTextColor(255, 255, 255); // White text
        doc.text(`Page ${i} of ${pageCount}`, 105, 286, { align: 'center' });
        doc.setTextColor(0, 0, 0); // Reset to black text
      } else {
        doc.setFontSize(8);
        doc.text(`Page ${i} of ${pageCount}`, 190, 285, { align: 'right' });
      }
    }

    // Return the doc object for direct saving
    return doc;
  };

  const onSubmit = async (data) => {
    if (selectedBookings.length === 0) {
      toast.error('Please select at least one booking');
      return;
    }

    setIsLoading(true);
    try {
      // If a preview has been done, generate exactly what was previewed
      if (lastPreviewPayload) {
        const { invoiceData, selectedBookingsData } = lastPreviewPayload;
        await createInvoice(user.id, invoiceData, selectedBookingsData);
        toast.success(`Invoice ${invoiceData.invoiceNo} generated successfully!`);
        // Reset preview cache to avoid stale reuse
        setLastPreviewPayload(null);
        // Reset for next invoice number
        const nextNo = await getNextInvoiceNo(user.id);
        setNextInvoiceNo(nextNo);
        try {
          setValue('invoiceNo', String(nextNo).toString().padStart(3, '0'), { shouldValidate: true });
        } catch (e) {
          // ignore if setValue not available
        }
        setIsLoading(false);
        return;
      }

      const selectedCustomer = customers.find(c => c.id === data.customerId);
      const selectedBookingsData = filteredBookings.filter(b => selectedBookings.includes(b.id));

      // Hard validations and safe defaults
      const safeInvoiceDate = data.invoiceDate || new Date().toISOString().split('T')[0];
      const safeFromDate = data.fromDate;
      const safeToDate = data.toDate;
      if (!selectedCustomer || !safeFromDate || !safeToDate) {
        toast.error('Please select customer and date range');
        setIsLoading(false);
        return;
      }

      const consignmentAmount = calculateConsignmentAmount();
      const docketCharge = parseFloat(data.docketCharge) || 0;
      const odaCharge = parseFloat(data.odaCharge) || 0;
      const otherCharge = parseFloat(data.otherCharge) || 0;
      const fuelChargePercent = sanitizePercentInput(data.fuelCharge);
      const royaltyChargePercent = sanitizePercentInput(data.royaltyCharge);

      const { baseRupees, fuelAmountRupees, royaltyAmountRupees, taxableValueRupees } = computeInvoiceCharges({
        consignmentAmount,
        odaCharges: odaCharge,
        docketCharges: docketCharge,
        otherCharges: otherCharge,
        fuelPercent: fuelChargePercent,
        royaltyPercent: royaltyChargePercent,
      });

      const taxableBase = baseRupees;
      const fuelCharge = fuelAmountRupees;
      const royaltyCharge = royaltyAmountRupees;

      const totalCharges = fuelCharge + royaltyCharge + docketCharge + odaCharge + otherCharge;
      const totalBeforeGST = taxableValueRupees;

      // Calculate GST based on customer state and user-provided percentage
      const userGstPercent = parseFloat(data.gstPercent) || 0;
      let gstCalculation;

      if (selectedCustomer?.customer_state === 'same') {
        // Same state: Split user GST percentage into CGST and SGST (half each)
        const cgstPercent = userGstPercent / 2;
        const sgstPercent = userGstPercent / 2;
        const cgstAmount = (totalBeforeGST * cgstPercent) / 100;
        const sgstAmount = (totalBeforeGST * sgstPercent) / 100;
        const totalGST = cgstAmount + sgstAmount;

        gstCalculation = {
          gstType: 'CGST+SGST',
          cgstPercent,
          sgstPercent,
          cgstAmount,
          sgstAmount,
          totalGST,
          gstPercent: userGstPercent
        };
      } else {
        // Other state: Use full user GST percentage as IGST
        const igstPercent = userGstPercent;
        const igstAmount = (totalBeforeGST * igstPercent) / 100;

        gstCalculation = {
          gstType: 'IGST',
          igstPercent,
          igstAmount,
          totalGST: igstAmount,
          gstPercent: userGstPercent
        };
      }

      const grandTotal = totalBeforeGST + gstCalculation.totalGST;

      // Ensure invoice number is numeric and padded to 3 digits
      const numericPart = ((data.invoiceNo || nextInvoiceNo) + '').replace(/\D/g, '').slice(0, 3).padStart(3, '0');
      const prefix = franchiseProfile?.invoicePrefix || 'INV';
      const invoiceNoPadded = `${prefix}${numericPart}`;

      const invoiceData = {
        ...data,
        invoiceNo: invoiceNoPadded,
        customerName: selectedCustomer?.customer_name,
        invoiceDate: safeInvoiceDate,
        fromDate: safeFromDate,
        toDate: safeToDate,
        subtotal: totalBeforeGST,
        fuelPercent: fuelChargePercent,
        royaltyPercent: royaltyChargePercent,
        fuelCharge,
        royaltyCharge,
        docketCharge,
        odaCharge,
        otherCharge,
        totalCharges,
        totalBeforeGST,
        gstCalculation,
        grandTotal,
        selectedBookings: selectedBookingsData,
        createdAt: new Date().toISOString()
      };

      // Save invoice in Supabase first
      if (isEditMode && editInvoiceId) {
        await updateInvoice(user.id, editInvoiceId, invoiceData, selectedBookingsData);
        toast.success(`Invoice ${invoiceData.invoiceNo} updated successfully!`);

        // Redirect to view invoices page after successful update
        navigate('/invoices');
        return;
      } else {
        // For new invoices, save to database only (no download/preview from generate)
        await createInvoice(user.id, invoiceData, selectedBookingsData);
        toast.success(`Invoice ${invoiceData.invoiceNo} generated successfully!`);

        // Build the same layout data if needed in future (kept for potential use)
        const invoiceForLayout = {
          number: `INV/2024-${nextInvoiceNo}`,
          date: data.invoiceDate,
          fromDate: data.fromDate,
          toDate: data.toDate,
          customer: {
            name: selectedCustomer.customer_name,
            address: selectedCustomer.address,
            gst: selectedCustomer.gst_no
          },
          days: getDaysInclusive(data.fromDate, data.toDate),
          consignment: selectedBookingsData.length,
          weight: selectedBookingsData
            .reduce((sum, booking) => sum + parseFloat(booking.weight || 0), 0)
            .toFixed(2),
          amount: consignmentAmount.toFixed(2),
          items: selectedBookingsData.map((booking) => ({
            date: booking.date,
            cnote: booking.awb_no || booking.awbNo,
            receiver: booking.receiver_name || booking.receiverName,
            destination: booking.receiver_address || booking.receiverAddress,
            mode: booking.mode,
            weight: booking.weight,
            qty: booking.quantity,
            type: booking.type,
            amount: parseFloat(booking.amount || 0).toFixed(2),
            other: parseFloat(booking.other_charge || 0).toFixed(2)
          })),
          subtotal: totalBeforeGST.toFixed(2),
          fuelCharge: fuelCharge.toFixed(2),
          royaltyCharge: royaltyCharge.toFixed(2),
          docketCharge: docketCharge.toFixed(2),
          odaCharge: odaCharge.toFixed(2),
          otherCharge: otherCharge.toFixed(2),
          gstType: gstCalculation.gstType,
          cgstPercent: gstCalculation.cgstPercent || 0,
          cgstAmount: gstCalculation.cgstAmount || 0,
          sgstPercent: gstCalculation.sgstPercent || 0,
          sgstAmount: gstCalculation.sgstAmount || 0,
          igstPercent: gstCalculation.igstPercent || 0,
          igstAmount: gstCalculation.igstAmount || 0,
          igst: gstCalculation.totalGST.toFixed(2),
          total: grandTotal.toFixed(2)
        };
        // Stay on the same page per requirement (no redirect)
      }

      // Reset form only for new invoices
      if (!isEditMode) {
        setSelectedBookings([]);
        setValue('customerId', '');
        setValue('fromDate', '');
        setValue('toDate', '');
        setValue('fuelCharge', '');
        setValue('royaltyCharge', '');
        setValue('docketCharge', '');
        setValue('odaCharge', '');
        setValue('otherCharge', '');
        setValue('gstPercent', '0');

        const nextNo = await getNextInvoiceNo(user.id);
        setNextInvoiceNo(nextNo);
        setValue('invoiceNo', nextNo.toString().padStart(3, '0'));
      }
    } catch (error) {
      console.error(error);
      toast.error(isEditMode ? `Failed to update invoice: ${error.message || ''}` : `Failed to generate invoice: ${error.message || ''}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getDaysInclusive = (from, to) => {
    try {
      const d1 = new Date(from);
      const d2 = new Date(to);
      const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
      return (isNaN(diff) ? 0 : diff) + 1;
    } catch {
      return 0;
    }
  };

  const handlePreview = () => {
    const data = {
      invoiceNo: watch('invoiceNo'),
      customerId: watch('customerId'),
      invoiceDate: watch('invoiceDate'),
      fromDate: watch('fromDate'),
      toDate: watch('toDate'),
      fuelCharge: parseFloat(watch('fuelCharge') || 0),
      royaltyCharge: parseFloat(watch('royaltyCharge') || 0),
      docketCharge: parseFloat(watch('docketCharge') || 0),
      odaCharge: parseFloat(watch('odaCharge') || 0),
      otherCharge: parseFloat(watch('otherCharge') || 0),
      gstPercent: parseFloat(watch('gstPercent') || 0)
    };

    if (selectedBookings.length === 0) {
      toast.error('Please select at least one booking');
      return;
    }

    const selectedCustomer = customers.find(c => c.id === data.customerId);
    if (!selectedCustomer) {
      toast.error('Please select a customer');
      return;
    }

    const selectedBookingsData = filteredBookings.filter(b => selectedBookings.includes(b.id));
    const consignmentAmount = calculateConsignmentAmount();

    const docketCharge = parseFloat(data.docketCharge) || 0;
    const odaCharge = parseFloat(data.odaCharge) || 0;
    const otherCharge = parseFloat(data.otherCharge) || 0;
    const fuelChargePercent = sanitizePercentInput(data.fuelCharge);
    const royaltyChargePercent = sanitizePercentInput(data.royaltyCharge);

    const { baseRupees, fuelAmountRupees, royaltyAmountRupees, taxableValueRupees } = computeInvoiceCharges({
      consignmentAmount,
      odaCharges: odaCharge,
      docketCharges: docketCharge,
      otherCharges: otherCharge,
      fuelPercent: fuelChargePercent,
      royaltyPercent: royaltyChargePercent,
    });

    const taxableBase = baseRupees;
    const fuelCharge = fuelAmountRupees;
    const royaltyCharge = royaltyAmountRupees;

    const totalCharges = fuelCharge + royaltyCharge + docketCharge + odaCharge + otherCharge;
    const totalBeforeGST = taxableValueRupees;

    // Calculate GST based on customer state and user-provided percentage
    const userGstPercent = parseFloat(data.gstPercent) || 0;
    let gstCalculation;

    if (selectedCustomer?.customer_state === 'same') {
      // Same state: Split user GST percentage into CGST and SGST (half each)
      const cgstPercent = userGstPercent / 2;
      const sgstPercent = userGstPercent / 2;
      const cgstAmount = (totalBeforeGST * cgstPercent) / 100;
      const sgstAmount = (totalBeforeGST * sgstPercent) / 100;
      const totalGST = cgstAmount + sgstAmount;

      gstCalculation = {
        gstType: 'CGST+SGST',
        cgstPercent,
        sgstPercent,
        cgstAmount,
        sgstAmount,
        totalGST,
        gstPercent: userGstPercent
      };
    } else {
      // Other state: Use full user GST percentage as IGST
      const igstPercent = userGstPercent;
      const igstAmount = (totalBeforeGST * igstPercent) / 100;

      gstCalculation = {
        gstType: 'IGST',
        igstPercent,
        igstAmount,
        totalGST: igstAmount,
        gstPercent: userGstPercent
      };
    }

    const grandTotal = totalBeforeGST + gstCalculation.totalGST;

    // Format data for the InvoiceLayout component
    const invoiceData = {
      number: `${(franchiseProfile?.invoicePrefix || 'INV')}${data.invoiceNo || nextInvoiceNo}`,
      date: data.invoiceDate,
      fromDate: data.fromDate,
      toDate: data.toDate,
      customer: {
        name: selectedCustomer.customer_name,
        address: selectedCustomer.address,
        gst: selectedCustomer.gst_no
      },
      days: getDaysInclusive(data.fromDate, data.toDate),
      consignment: selectedBookingsData.length,
      weight: selectedBookingsData.reduce((sum, booking) => sum + parseFloat(booking.weight || 0), 0).toFixed(2),
      amount: consignmentAmount.toFixed(2),
      items: selectedBookingsData.map((booking, index) => ({
        date: booking.date,
        cnote: booking.awb_no || booking.awbNo,
        receiver: booking.receiver_name || booking.receiverName,
        destination: booking.receiver_address || booking.receiverAddress,
        mode: booking.mode,
        weight: booking.weight,
        qty: booking.quantity,
        type: booking.type,
        amount: parseFloat(booking.amount || 0).toFixed(2),
        other: parseFloat(booking.other_charge || 0).toFixed(2)
      })),
      subtotal: totalBeforeGST.toFixed(2),
      fuelPercent: fuelChargePercent,
      royaltyPercent: royaltyChargePercent,
      fuelCharge: fuelCharge.toFixed(2),
      royaltyCharge: royaltyCharge.toFixed(2),
      docketCharge: docketCharge.toFixed(2),
      odaCharge: odaCharge.toFixed(2),
      otherCharge: otherCharge.toFixed(2),
      gstType: gstCalculation.gstType,
      cgstPercent: gstCalculation.cgstPercent || 0,
      cgstAmount: gstCalculation.cgstAmount || 0,
      sgstPercent: gstCalculation.sgstPercent || 0,
      sgstAmount: gstCalculation.sgstAmount || 0,
      igstPercent: gstCalculation.igstPercent || 0,
      igstAmount: gstCalculation.igstAmount || 0,
      igst: gstCalculation.totalGST.toFixed(2),
      total: grandTotal.toFixed(2)
    };

    setPreviewInvoice(invoiceData);
    setShowPreview(true);
    // Persist exact data for generate
    const safeInvoiceDate = data.invoiceDate || new Date().toISOString().split('T')[0];
    const dbInvoiceData = {
      ...data,
      invoiceNo: nextInvoiceNo,
      customerId: data.customerId,
      invoiceDate: safeInvoiceDate,
      fromDate: data.fromDate,
      toDate: data.toDate,
      subtotal: totalBeforeGST,
      fuelPercent: fuelChargePercent,
      royaltyPercent: royaltyChargePercent,
      fuelCharge,
      royaltyCharge,
      docketCharge,
      odaCharge,
      otherCharge,
      totalCharges,
      totalBeforeGST,
      gstCalculation,
      grandTotal,
    };
    setLastPreviewPayload({ invoiceData: dbInvoiceData, selectedBookingsData });
  };

  // Preview is view-only; printing/downloading disabled per requirements
  const handlePrint = null;

  return (
    <>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? 'Edit Invoice' : 'Invoice Generator'}
          </h1>
          <p className="text-gray-600">
            {isEditMode
              ? 'Update customer, date range, charges and save changes'
              : 'Select customer, date range, enter charges and generate invoice'}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Invoice Details */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Invoice Details
              </h3>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invoice Number *
                  </label>
                  <div className="flex rounded-md shadow-sm">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 font-medium sm:text-sm">
                      {franchiseProfile?.invoicePrefix || 'INV'}
                    </span>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      {...register('invoiceNo', {
                        required: 'Invoice number is required',
                        maxLength: { value: 3, message: 'Invoice number must be 3 digits or less' },
                        pattern: { value: /^\d{1,3}$/, message: 'Invoice number must be numeric (up to 3 digits)' }
                      })}
                      maxLength={3}
                      className="flex-1 w-full px-3 py-2 border border-gray-300 rounded-none rounded-r-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono font-bold text-primary-600"
                      placeholder="001"
                      defaultValue={nextInvoiceNo.toString().padStart(3, '0')}
                      onBlur={() => {
                        // sanitize and pad to 3 digits on blur
                        try {
                          const v = (watch('invoiceNo') || '').toString();
                          const digits = v.replace(/\D/g, '').slice(0, 3);
                          const padded = digits.padStart(3, '0');
                          setValue('invoiceNo', padded, { shouldValidate: true });
                          setNextInvoiceNo(padded);
                        } catch (e) {
                          // ignore
                        }
                      }}
                    />
                  </div>
                  {errors.invoiceNo && (
                    <p className="mt-1 text-sm text-red-600">{errors.invoiceNo.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Invoice Date *
                  </label>
                  <input
                    type="date"
                    {...register('invoiceDate', { required: 'Invoice date is required' })}
                    onClick={(e) => { try { if (e.currentTarget.showPicker) e.currentTarget.showPicker(); } catch (_) { } }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    defaultValue={new Date().toISOString().split('T')[0]}
                  />
                  {errors.invoiceDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.invoiceDate.message}</p>
                  )}
                </div>
              </div>

              <div>
                <CustomerAutocomplete
                  label="Customer *"
                  placeholder="Type customer ID"
                  value={watch('customerId')}
                  valueLabel={customers.find(c => c.id === watch('customerId'))?.customer_name || ''}
                  onChange={(c) => setValue('customerId', c.id, { shouldValidate: true })}
                  error={errors.customerId?.message}
                />
                <input type="hidden" {...register('customerId', { required: 'Please select a customer' })} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From Date *
                  </label>
                  <input
                    type="date"
                    {...register('fromDate', { required: 'From date is required' })}
                    onClick={(e) => { try { if (e.currentTarget.showPicker) e.currentTarget.showPicker(); } catch (_) { } }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {errors.fromDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.fromDate.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To Date *
                  </label>
                  <input
                    type="date"
                    {...register('toDate', { required: 'To date is required' })}
                    onClick={(e) => { try { if (e.currentTarget.showPicker) e.currentTarget.showPicker(); } catch (_) { } }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {errors.toDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.toDate.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Bookings Selection */}
          {filteredBookings.length > 0 && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <Package className="h-5 w-5 mr-2" />
                    Available Bookings ({filteredBookings.length})
                  </h3>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={selectAllBookings}
                      className="px-3 py-1 text-sm bg-primary-100 text-primary-700 rounded-md hover:bg-primary-200"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={clearSelection}
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {filteredBookings.map((booking) => (
                    <div
                      key={booking.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-colors ${selectedBookings.includes(booking.id)
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                        }`}
                      onClick={() => toggleBookingSelection(booking.id)}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="font-medium">AWB #{booking.awb_no || booking.awbNo}</span>
                          <span className="ml-2 text-sm text-gray-500">
                            {(booking.receiver_name || booking.receiverName)} - {booking.mode}
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="font-medium">₹{parseFloat(booking.amount).toFixed(2)}</span>
                          <div className="text-sm text-gray-500">{booking.date}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Charges */}
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Calculator className="h-5 w-5 mr-2" />
                Additional Charges
              </h3>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fuel Charge (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('fuelCharge', {
                      min: { value: 0, message: 'Fuel % cannot be negative' },
                      max: { value: 100, message: 'Fuel % cannot exceed 100' }
                    })}
                    min={0}
                    max={100}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Royalty Charge (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('royaltyCharge', {
                      min: { value: 0, message: 'Royalty % cannot be negative' },
                      max: { value: 100, message: 'Royalty % cannot exceed 100' }
                    })}
                    min={0}
                    max={100}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Docket Charge (₹)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('docketCharge', {
                      min: { value: 0, message: 'Docket charge cannot be negative' },
                      max: { value: 99999999.99, message: 'Docket charge too large' }
                    })}
                    min={0}
                    max={99999999.99}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ODA Charge (₹)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('odaCharge', {
                      min: { value: 0, message: 'ODA charge cannot be negative' },
                      max: { value: 99999999.99, message: 'ODA charge too large' }
                    })}
                    min={0}
                    max={99999999.99}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Other Charge (₹)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('otherCharge', {
                      min: { value: 0, message: 'Other charge cannot be negative' },
                      max: { value: 99999999.99, message: 'Other charge too large' }
                    })}
                    min={0}
                    max={99999999.99}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GST Percentage (%)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    {...register('gstPercent', {
                      min: { value: 0, message: 'GST % cannot be negative' },
                      max: { value: 100, message: 'GST % cannot exceed 100' }
                    })}
                    defaultValue="0"
                    min={0}
                    max={100}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Summary */}
              {selectedBookings.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Summary</h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Selected Bookings:</span>
                      <span className="ml-2 font-medium">{selectedBookings.length}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Consignment Amount:</span>
                      <span className="ml-2 font-medium">₹{calculateConsignmentAmount().toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Additional Charges:</span>
                      <span className="ml-2 font-medium">₹{(
                        (parseFloat(watch('fuelCharge') || 0) +
                          parseFloat(watch('royaltyCharge') || 0) +
                          parseFloat(watch('docketCharge') || 0) +
                          parseFloat(watch('odaCharge') || 0) +
                          parseFloat(watch('otherCharge') || 0))
                      ).toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Taxable Value:</span>
                      <span className="ml-2 font-medium">₹{calculateTaxableBase().toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">GST Type:</span>
                      <span className="ml-2 font-medium">
                        {selectedBookings.length > 0 && filteredBookings.length > 0 ?
                          (() => {
                            const selectedCustomer = customers.find(c => c.id === watch('customerId'));
                            const userGstPercent = parseFloat(watch('gstPercent')) || 0;
                            if (selectedCustomer?.customer_state === 'same') {
                              const halfPercent = userGstPercent / 2;
                              return `CGST + SGST (${halfPercent}% + ${halfPercent}%)`;
                            } else {
                              return `IGST (${userGstPercent}%)`;
                            }
                          })() :
                          `IGST (${parseFloat(watch('gstPercent')) || 0}%)`
                        }
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">

            <button
              type="button"
              onClick={handlePreview}
              disabled={isLoading || selectedBookings.length === 0}
              className="flex items-center px-6 py-3 bg-white text-primary-600 border border-primary-600 font-medium rounded-md hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Eye className="h-5 w-5 mr-2" />
              Preview Invoice
            </button>
            <button
              type="submit"
              disabled={isLoading || selectedBookings.length === 0}
              className="flex items-center px-6 py-3 bg-primary-600 text-white font-medium rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isEditMode ? <Save className="h-5 w-5 mr-2" /> : <Download className="h-5 w-5 mr-2" />}
              {isLoading
                ? (isEditMode ? 'Updating...' : 'Generating...')
                : (isEditMode ? 'Update Invoice' : 'Generate Invoice')}
            </button>
          </div>
        </form>

        {/* Page footer */}
        <footer className="mt-8 border-t border-gray-200">
          <div className="py-4 text-center text-xs text-gray-500">
            Powered by SoftNex
          </div>
        </footer>
      </div>

      {/* Invoice Preview Modal */}
      {showPreview && previewInvoice && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">Invoice Preview</h2>
                <button
                  onClick={() => setShowPreview(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* Preview-only: no print/download actions */}

                <div ref={printComponentRef}>
                  <InvoiceLayout invoice={previewInvoice} profile={franchiseProfile} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InvoiceGenerator;